import React, { ReactNode } from 'react'
import { Indicator, Modal, SectionTitle } from 'is24-corecss'
import { Trans, useTranslation } from 'react-i18next'
import {
  IconS24AlertShield24,
  IconS24BuildingMagnifier24,
  IconS24MapEuro24,
  IconS24UsersThree24,
} from '@is24/cosma-ui-icons'
import { useAppSelector } from '../../../../../../../app/hooks'
import { InitState } from '../../../../../../../redux/reducers/root/initialState'
import styles from './RecommendedPriceNotificationModal.module.css'

export type RecommendedPriceNotificationModalVariant = 'fairPrice' | 'priceAboveRange'
type IndicatorColor = 'blue' | 'green' | 'red' | 'yellow'

type RecommendedPriceNotificationModalProps = {
  isOpen: boolean
  onClose: () => void
  variant: RecommendedPriceNotificationModalVariant
  minValue: number
  maxValue: number
  price?: number
}

type RecommendedPriceTileProps = {
  title: string
  description: ReactNode
  indicatorLabel: string
  indicatorColor: IndicatorColor
  icon: ReactNode
}

function RecommendedPriceTile({ title, description, indicatorLabel, indicatorColor, icon }: RecommendedPriceTileProps) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileIcon}>{icon}</div>
      <p className={`font-body-medium-bold ${styles.tileTitle}`}>{title}</p>
      <p className={`font-body-medium-regular ${styles.tileDescription}`}>{description}</p>
      <Indicator appearance='label' labelColor={indicatorColor} labelVariant='flat' className={styles.tileIndicator}>
        {indicatorLabel}
      </Indicator>
    </div>
  )
}

const LEGAL_INDICATOR_COLOR: IndicatorColor = 'blue'

const getSavedSearchesLevel = (savedSearches: number) => {
  if (savedSearches >= 50) {
    return {
      descriptionKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.description.high',
      indicatorColor: 'green' as const,
      indicatorKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.indicator.high',
    }
  }

  if (savedSearches >= 15) {
    return {
      descriptionKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.description.medium',
      indicatorColor: 'yellow' as const,
      indicatorKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.indicator.medium',
    }
  }

  return {
    descriptionKey:
      'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.description.low',
    indicatorColor: 'red' as const,
    indicatorKey:
      'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.indicator.low',
  }
}

const getSimilarObjectsLevel = (similarObjects: number) => {
  if (similarObjects >= 25) {
    return {
      descriptionKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.description.high',
      indicatorKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.indicator.high',
    }
  }

  if (similarObjects >= 5) {
    return {
      descriptionKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.description.medium',
      indicatorKey:
        'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.indicator.medium',
    }
  }

  return {
    descriptionKey:
      'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.description.low',
    indicatorKey:
      'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.indicator.low',
  }
}

export function RecommendedPriceNotificationModal({
  isOpen,
  onClose,
  variant,
  minValue,
  maxValue,
  price,
}: RecommendedPriceNotificationModalProps) {
  const { t, i18n } = useTranslation()
  const { rentControlApplies } = useAppSelector((state: InitState) => state.rentControlFeature)
  const { savedSearches, similarObjects } = useAppSelector((state: InitState) => state.priceMaximizerFeature)

  const formatNumber = (value: number) => value.toLocaleString(i18n.language)
  const formattedMinValue = formatNumber(minValue)
  const formattedMaxValue = formatNumber(maxValue)
  const formattedPrice = price === undefined ? undefined : formatNumber(price)
  const showLegalTiles = typeof rentControlApplies === 'boolean'
  const showSupplyDemandTiles = savedSearches !== undefined && similarObjects !== undefined
  const savedSearchesLevel = showSupplyDemandTiles ? getSavedSearchesLevel(savedSearches) : undefined
  const similarObjectsLevel = showSupplyDemandTiles ? getSimilarObjectsLevel(similarObjects) : undefined

  return (
    <Modal
      visible={isOpen}
      width={662}
      ariaLabelCloseButton='close'
      title={t('wizard.screen.basicInfo.price.recommendedNotification.modal.title')}
      onClose={onClose}
      data-testid='recommended-price-notification-modal'
    >
      <div className={`font-default-color ${styles.content}`}>
        <div className={styles.introSection}>
          <p className={`font-body-medium-regular ${styles.intro}`}>
            {variant === 'fairPrice' && formattedPrice && (
              <Trans
                i18nKey='wizard.screen.basicInfo.price.recommendedNotification.modal.intro.fairPrice'
                values={{ price: formattedPrice, minValue: formattedMinValue, maxValue: formattedMaxValue }}
                components={{ strong: <strong /> }}
              />
            )}
            {variant === 'fairPrice' && !formattedPrice && (
              <Trans
                i18nKey='wizard.screen.basicInfo.price.recommendedNotification.modal.intro.fairPriceWithoutPrice'
                values={{ minValue: formattedMinValue, maxValue: formattedMaxValue }}
                components={{ strong: <strong /> }}
              />
            )}
            {variant === 'priceAboveRange' && formattedPrice && (
              <Trans
                i18nKey='wizard.screen.basicInfo.price.recommendedNotification.modal.intro.priceAboveRange'
                values={{ price: formattedPrice, minValue: formattedMinValue, maxValue: formattedMaxValue }}
                components={{ strong: <strong /> }}
              />
            )}
          </p>
          <p className={`font-body-medium-regular ${styles.note}`}>
            {t('wizard.screen.basicInfo.price.recommendedNotification.modal.note')}
          </p>
        </div>

        {showLegalTiles && (
          <div className={styles.section}>
            <SectionTitle
              title={t('wizard.screen.basicInfo.price.recommendedNotification.modal.legal.title')}
              size='small'
              variant='bold'
            />
            <div className={styles.tiles}>
              <RecommendedPriceTile
                icon={<IconS24MapEuro24 />}
                title={t('wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentIndex.title')}
                description={t(
                  rentControlApplies
                    ? 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentIndex.description.present'
                    : 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentIndex.description.missing'
                )}
                indicatorColor={LEGAL_INDICATOR_COLOR}
                indicatorLabel={t(
                  rentControlApplies
                    ? 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentIndex.indicator.present'
                    : 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentIndex.indicator.missing'
                )}
              />
              <RecommendedPriceTile
                icon={<IconS24AlertShield24 />}
                title={t('wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentControl.title')}
                description={t(
                  rentControlApplies
                    ? 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentControl.description.active'
                    : 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentControl.description.inactive'
                )}
                indicatorColor={LEGAL_INDICATOR_COLOR}
                indicatorLabel={t(
                  rentControlApplies
                    ? 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentControl.indicator.active'
                    : 'wizard.screen.basicInfo.price.recommendedNotification.modal.legal.rentControl.indicator.inactive'
                )}
              />
            </div>
          </div>
        )}

        {showSupplyDemandTiles && savedSearchesLevel && similarObjectsLevel && (
          <div className={styles.section}>
            <SectionTitle
              title={t('wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.title')}
              size='small'
              variant='bold'
            />
            <div className={styles.tiles}>
              <RecommendedPriceTile
                icon={<IconS24UsersThree24 />}
                title={t(
                  'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.savedSearches.title'
                )}
                description={
                  <Trans
                    i18nKey={savedSearchesLevel.descriptionKey}
                    values={{ formattedCount: formatNumber(savedSearches) }}
                    components={{ strong: <strong /> }}
                  />
                }
                indicatorColor={savedSearchesLevel.indicatorColor}
                indicatorLabel={t(savedSearchesLevel.indicatorKey)}
              />
              <RecommendedPriceTile
                icon={<IconS24BuildingMagnifier24 />}
                title={t(
                  'wizard.screen.basicInfo.price.recommendedNotification.modal.supplyDemand.similarObjects.title'
                )}
                description={
                  <Trans
                    i18nKey={similarObjectsLevel.descriptionKey}
                    values={{ formattedCount: formatNumber(similarObjects) }}
                    components={{ strong: <strong /> }}
                  />
                }
                indicatorColor='blue'
                indicatorLabel={t(similarObjectsLevel.indicatorKey)}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
